<template>
  <div>
    <CrmPageHeader
      title="Visão geral CRM"
      :subtitle="`${periodoLabel} · funil, tarefas e agenda comercial`"
    >
      <template #actions>
        <button
          class="btn btn-light-success btn-sm"
          type="button"
          @click="abrirOportunidadesGanhas"
        >
          Ganhas ({{ oportunidadesGanhas.length }})
        </button>
        <router-link class="btn btn-dark btn-sm" :to="{ name: 'crmFunil' }">
          Abrir funil
        </router-link>
        <router-link
          class="btn btn-light-primary btn-sm"
          :to="{ name: 'crmAgenda' }"
        >
          Abrir agenda
        </router-link>
        <router-link
          class="btn btn-light btn-sm"
          :to="{ name: 'crmRelatorios' }"
        >
          Relatórios
        </router-link>
      </template>
    </CrmPageHeader>

    <div class="card card-custom gutter-b">
      <div class="card-body">
        <b-row>
          <b-col md="4" lg="2">
            <b-form-group label="Data inicial">
              <b-form-input v-model="filtros.data_inicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="2">
            <b-form-group label="Data final">
              <b-form-input v-model="filtros.data_fim" type="date" />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="2">
            <b-form-group label="Funil">
              <b-form-select
                v-model="filtros.id_crm_funil"
                :options="funilOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="2">
            <b-form-group label="Responsável">
              <b-form-select
                v-model="filtros.id_usuario"
                :options="usuarioOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="2">
            <b-form-group label="Status">
              <b-form-select
                v-model="filtros.status"
                :options="statusOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="2">
            <b-form-group label="Temperatura">
              <b-form-select
                v-model="filtros.temperatura"
                :options="temperaturaOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="2" class="d-flex align-items-end">
            <button
              class="btn btn-light-primary btn-block mb-7"
              type="button"
              :disabled="loading"
              @click="loadDashboard"
            >
              Atualizar
            </button>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-row>
      <b-col md="6" lg="4" xl="2" v-for="card in cards" :key="card.label">
        <router-link class="crm-metric-card" :to="card.to">
          <span>{{ card.label }}</span>
          <strong>{{ card.value }}</strong>
          <small>{{ card.hint }}</small>
        </router-link>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="6">
        <div class="card card-custom gutter-b">
          <div class="card-header">
            <div class="card-title">
              <h3 class="card-label">Próximas tarefas</h3>
            </div>
            <div class="card-toolbar">
              <router-link
                class="btn btn-light-primary btn-sm"
                :to="{ name: 'crmTarefas' }"
              >
                Abrir tarefas
              </router-link>
            </div>
          </div>
          <div class="card-body">
            <div v-if="tarefasCriticas.length" class="crm-critical-list">
              <button
                v-for="item in tarefasCriticas"
                :key="item.id_crm_atividade"
                class="crm-critical-item"
                type="button"
                @click="abrirTarefa(item)"
              >
                <div>
                  <strong>{{ item.titulo }}</strong>
                  <small>
                    {{ item.oportunidade || item.contato || "Sem cliente" }} ·
                    {{ formatDateTime(item.vencimento_em) }}
                  </small>
                  <span>
                    {{ item.tipo || "Tarefa" }} ·
                    {{ item.usuario || "Sem responsável" }}
                  </span>
                </div>
                <div
                  class="crm-status-pill"
                  :class="{ 'crm-status-pill-danger': item.atrasada }"
                >
                  {{ item.atrasada ? "Atrasada" : "Próxima" }}
                </div>
              </button>
            </div>
            <div v-else class="crm-empty-state">
              Nenhuma tarefa próxima exigindo atenção.
            </div>
          </div>
        </div>
      </b-col>
      <b-col lg="6">
        <div class="card card-custom gutter-b">
          <div class="card-header">
            <div class="card-title">
              <h3 class="card-label">Agenda próxima</h3>
            </div>
            <div class="card-toolbar">
              <router-link
                class="btn btn-light-primary btn-sm"
                :to="{ name: 'crmAgenda' }"
              >
                Abrir agenda
              </router-link>
            </div>
          </div>
          <div class="card-body">
            <div v-if="agendaCritica.length" class="crm-agenda-list">
              <button
                v-for="item in agendaCritica"
                :key="item.id_crm_agenda"
                class="crm-agenda-item"
                type="button"
                @click="abrirAgenda(item)"
              >
                <strong>{{ formatDateTime(item.data_inicio) }}</strong>
                <span>{{ item.titulo }}</span>
                <small>
                  {{ item.oportunidade || item.contato || "Sem cliente" }} ·
                  {{ statusAgendaLabel(item.status) }}
                </small>
              </button>
            </div>
            <div v-else class="crm-empty-state">
              Nenhum compromisso próximo exigindo atenção.
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="7">
        <div class="card card-custom gutter-b">
          <div class="card-header">
            <div class="card-title">
              <h3 class="card-label">Oportunidades em atenção</h3>
            </div>
            <div class="card-toolbar text-muted">Score de prioridade</div>
          </div>
          <div class="card-body">
            <div v-if="oportunidadesCriticas.length" class="crm-critical-list">
              <button
                v-for="item in oportunidadesCriticas"
                :key="item.id_crm_oportunidade"
                class="crm-critical-item"
                type="button"
                @click="abrirOportunidade(item)"
              >
                <div>
                  <strong>{{ item.titulo }}</strong>
                  <small>
                    {{ item.contato || item.pessoa || "Sem contato" }} ·
                    {{ item.etapa || "Sem etapa" }}
                  </small>
                  <span>{{ prioridadeMotivos(item) }}</span>
                </div>
                <div class="crm-score">
                  {{ item.score_prioridade || 0 }}
                </div>
              </button>
            </div>
            <div v-else class="crm-empty-state">
              Nenhuma oportunidade crítica nos filtros atuais.
            </div>
          </div>
        </div>
      </b-col>
      <b-col lg="5">
        <div class="card card-custom gutter-b">
          <div class="card-header">
            <div class="card-title">
              <h3 class="card-label">Atalhos CRM</h3>
            </div>
          </div>
          <div class="card-body">
            <div class="crm-action-list">
              <router-link :to="{ name: 'crmFunil' }">
                <strong>Funil</strong>
                <span>{{ funilAtalhoLabel }}</span>
              </router-link>
              <router-link :to="{ name: 'crmAgenda' }">
                <strong>Agenda</strong>
                <span>{{ agendaProximaLabel }}</span>
              </router-link>
              <router-link :to="{ name: 'crmRelatorios' }">
                <strong>Relatórios</strong>
                <span>Gráficos e análises completos por período.</span>
              </router-link>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-modal
      id="crm-ganhas-modal"
      title="Oportunidades ganhas"
      size="lg"
      hide-footer
    >
      <div v-if="oportunidadesGanhas.length" class="crm-won-list">
        <button
          v-for="item in oportunidadesGanhas"
          :key="item.id_crm_oportunidade"
          class="crm-won-item"
          type="button"
          @click="abrirOportunidadeGanha(item)"
        >
          <div>
            <strong>{{ item.titulo }}</strong>
            <small>
              {{ item.contato || item.pessoa || "Sem contato" }} ·
              {{ formatDateTime(item.ganho_em) }}
            </small>
            <span>
              {{ item.funil || "Funil" }} ·
              {{ item.etapa || "Fechamento" }}
            </span>
          </div>
          <div class="crm-won-meta">
            <strong>{{ formatMoney(item.valor_principal || 0) }}</strong>
            <small>
              {{ item.contrato_label || statusContratoLabel(item) }}
            </small>
          </div>
        </button>
      </div>
      <div v-else class="crm-empty-state">
        Nenhuma oportunidade ganha encontrada nos filtros atuais.
      </div>
    </b-modal>
  </div>
</template>

<script>
import CrmPageHeader from "@/modules/CRM/components/CrmPageHeader";
import crmService from "@/modules/CRM/services/crmService";

export default {
  name: "crm-dashboard",
  components: { CrmPageHeader },
  data() {
    return {
      filtros: this.defaultPeriodo(),
      funis: [],
      loading: false,
      resumo: null,
      statusOptions: [
        { value: "Todos", text: "Todos" },
        { value: "aberta", text: "Abertas" },
        { value: "ganha", text: "Ganhas" },
        { value: "perdida", text: "Perdidas" },
      ],
      temperaturas: [],
      usuarios: [],
    };
  },
  computed: {
    cards() {
      const resumo = this.resumo || {};
      return [
        {
          label: "Abertas",
          value: resumo.oportunidades_abertas || 0,
          hint: "Oportunidades em andamento",
          to: { name: "crmFunil" },
        },
        {
          label: "Valor aberto",
          value: this.formatMoney(resumo.valor_aberto || 0),
          hint: "Receita potencial",
          to: { name: "crmFunil" },
        },
        {
          label: "Próximas tarefas",
          value: this.tarefasCriticas.length,
          hint: this.tarefasAtrasadasLabel,
          to: { name: "crmTarefas" },
        },
        {
          label: "Agenda próxima",
          value: this.agendaCritica.length,
          hint: this.agendaProximaLabel,
          to: { name: "crmAgenda" },
        },
        {
          label: "Paradas",
          value: resumo.oportunidades_paradas || 0,
          hint: "Sem movimento há 7+ dias",
          to: { name: "crmFunil" },
        },
        {
          label: "Sem próxima ação",
          value: resumo.oportunidades_sem_proxima_acao || 0,
          hint: "Sem data definida para agir",
          to: { name: "crmFunil" },
        },
      ];
    },
    agendaProximaLabel() {
      const total = this.agendaCritica.length;
      return total
        ? `${total} compromisso(s) próximo(s) em destaque.`
        : "Sem compromisso crítico próximo.";
    },
    agendaCritica() {
      return this.resumo?.agenda_critica || [];
    },
    funilAtalhoLabel() {
      const resumo = this.resumo || {};
      return `${resumo.oportunidades_abertas || 0} aberta(s) · ${
        resumo.oportunidades_paradas || 0
      } parada(s).`;
    },
    funilOptions() {
      return [{ value: null, text: "Todos" }].concat(
        this.funis.map((funil) => ({
          value: funil.id_crm_funil,
          text: funil.nome,
        }))
      );
    },
    oportunidadesCriticas() {
      return this.resumo?.oportunidades_criticas || [];
    },
    oportunidadesGanhas() {
      return this.resumo?.oportunidades_ganhas || [];
    },
    periodoLabel() {
      return `${this.formatDate(
        this.filtros.data_inicio
      )} até ${this.formatDate(this.filtros.data_fim)}`;
    },
    tarefasCriticas() {
      return this.resumo?.tarefas_criticas || [];
    },
    tarefasAtrasadasLabel() {
      const total = this.resumo?.tarefas_atrasadas || 0;
      return total
        ? `${total} tarefa(s) vencida(s) aguardando ação.`
        : "Nenhuma tarefa atrasada.";
    },
    temperaturaOptions() {
      return [{ value: null, text: "Todas" }].concat(
        this.temperaturas.map((temperatura) => ({
          value: temperatura.chave,
          text: temperatura.nome,
        }))
      );
    },
    usuarioOptions() {
      return [{ value: null, text: "Todos" }].concat(
        this.usuarios.map((usuario) => ({
          value: usuario.id_usuario,
          text: usuario.nome || usuario.usuario,
        }))
      );
    },
  },
  mounted() {
    this.loadBase();
  },
  methods: {
    abrirAgenda(item) {
      this.$router.push({
        name: "crmAgendaEditar",
        params: { id: item.id_crm_agenda },
      });
    },
    abrirOportunidadesGanhas() {
      this.$bvModal.show("crm-ganhas-modal");
    },
    abrirOportunidade(item) {
      this.$router.push({
        name: "crmOportunidadeDetalhe",
        params: { id: item.id_crm_oportunidade },
      });
    },
    abrirOportunidadeGanha(item) {
      this.$bvModal.hide("crm-ganhas-modal");
      this.$router.push({
        name: "crmOportunidadeDetalhe",
        params: { id: item.id_crm_oportunidade },
        query: { origem: "ganhas" },
      });
    },
    abrirTarefa(item) {
      if (item.id_crm_oportunidade) {
        this.$router.push({
          name: "crmOportunidadeDetalhe",
          params: { id: item.id_crm_oportunidade },
        });
        return;
      }

      this.$router.push({ name: "crmTarefas" });
    },
    async loadBase() {
      await Promise.all([
        this.loadFunisFiltro(),
        this.loadUsuariosFiltro(),
        this.loadTemperaturasFiltro(),
      ]);

      await this.loadDashboard();
    },
    async loadFunisFiltro() {
      try {
        const funis = await crmService.funis();
        this.funis = this.normalizeCollection(this.responseData(funis));
      } catch (error) {
        this.funis = [];
        this.$toast.error("Não foi possível carregar os funis do CRM.");
      }
    },
    async loadUsuariosFiltro() {
      try {
        const usuarios = await crmService.usuarios({
          per_page: 100,
          page: 1,
          sort: [{ field: "nome", type: "asc" }],
        });
        this.usuarios = this.normalizeCollection(this.responseData(usuarios));
      } catch (error) {
        this.usuarios = [];
      }
    },
    async loadTemperaturasFiltro() {
      try {
        const temperaturas = await crmService.temperaturasConfiguracao();
        this.temperaturas = this.normalizeCollection(
          this.responseData(temperaturas)
        );
      } catch (error) {
        this.temperaturas = [];
      }
    },
    async loadDashboard() {
      if (!this.filtros.data_inicio || !this.filtros.data_fim) {
        this.$toast.error("Informe o período para carregar o dashboard.");
        return;
      }

      this.loading = true;
      try {
        const { data } = await crmService.dashboard(this.filtros);
        this.resumo = data;
      } catch (error) {
        this.$toast.error("Não foi possível carregar o dashboard CRM.");
      } finally {
        this.loading = false;
      }
    },
    defaultPeriodo() {
      const hoje = new Date();
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      return {
        data_inicio: this.toDateInput(inicio),
        data_fim: this.toDateInput(hoje),
        id_crm_funil: null,
        id_usuario: null,
        status: "Todos",
        temperatura: null,
      };
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR");
    },
    formatDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      return [];
    },
    prioridadeMotivos(item) {
      if (
        Array.isArray(item.motivos_prioridade) &&
        item.motivos_prioridade.length
      ) {
        return item.motivos_prioridade.join(" · ");
      }

      if (item.dias_parada) {
        return `Parada há ${item.dias_parada} dia(s)`;
      }

      return "Abrir para revisar próxima ação";
    },
    responseData(response) {
      const data = response ? response.data : null;
      return data && data.data ? data.data : data || {};
    },
    statusAgendaLabel(status) {
      const labels = {
        agendado: "Agendado",
        concluido: "Concluído",
        cancelado: "Cancelado",
        nao_compareceu: "Não compareceu",
      };
      return labels[status] || status || "Sem status";
    },
    statusContratoLabel(item) {
      if (!item || !item.id_crm_oportunidade_contrato) {
        return "Sem contrato gerado";
      }

      if (item.contrato_status === "ganha") {
        return "Contrato assinado";
      }

      return "Aguardando assinatura";
    },
    toDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.crm-page-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.crm-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.crm-metric-card {
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #3f4254;
  display: block;
  margin-bottom: 20px;
  min-height: 128px;
  padding: 18px;
}

.crm-metric-card:hover {
  border-color: #3699ff;
  color: #3f4254;
  text-decoration: none;
}

.crm-metric-card span,
.crm-metric-card small {
  color: #7e8299;
  display: block;
}

.crm-metric-card strong {
  display: block;
  font-size: 22px;
  margin: 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm-action-list {
  display: grid;
  gap: 10px;
}

.crm-action-list a,
.crm-agenda-item,
.crm-critical-item {
  background: #f3f6f9;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #3f4254;
  display: block;
  padding: 12px;
  text-align: left;
  width: 100%;
}

.crm-action-list a:hover,
.crm-agenda-item:hover,
.crm-critical-item:hover {
  border-color: #3699ff;
  color: #3f4254;
  text-decoration: none;
}

.crm-action-list span,
.crm-agenda-item small,
.crm-critical-item small,
.crm-critical-item span {
  color: #7e8299;
  display: block;
  margin-top: 3px;
}

.crm-critical-list {
  display: grid;
  gap: 8px;
}

.crm-won-list {
  display: grid;
  gap: 8px;
}

.crm-critical-item {
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.crm-critical-item > div:first-child {
  min-width: 0;
}

.crm-critical-item strong,
.crm-agenda-item span {
  overflow-wrap: anywhere;
}

.crm-score {
  align-items: center;
  background: #fff4de;
  border-radius: 999px;
  color: #b36b00;
  display: flex;
  flex: 0 0 44px;
  font-weight: 700;
  height: 44px;
  justify-content: center;
}

.crm-agenda-list {
  display: grid;
  gap: 8px;
}

.crm-agenda-item {
  cursor: pointer;
}

.crm-won-item {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #3f4254;
  cursor: pointer;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px;
  text-align: left;
  width: 100%;
}

.crm-won-item:hover {
  border-color: #1bc5bd;
}

.crm-won-item > div:first-child {
  min-width: 0;
}

.crm-won-item strong,
.crm-won-item small,
.crm-won-item span,
.crm-won-meta strong,
.crm-won-meta small {
  display: block;
}

.crm-won-item small,
.crm-won-item span,
.crm-won-meta small {
  color: #7e8299;
  margin-top: 3px;
}

.crm-won-meta {
  flex: 0 0 170px;
  text-align: right;
}

.crm-agenda-item span {
  display: block;
  font-weight: 600;
  margin-top: 3px;
}

.crm-empty-state {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  padding: 20px;
}

.crm-status-pill {
  align-items: center;
  background: #e1f0ff;
  border-radius: 999px;
  color: #3699ff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  justify-content: center;
  min-width: 82px;
  padding: 6px 10px;
}

.crm-status-pill-danger {
  background: #ffe2e5;
  color: #f64e60;
}

@media (max-width: 767px) {
  .crm-page-header {
    display: block;
  }

  .crm-header-actions {
    justify-content: flex-start;
    margin-top: 12px;
  }
}
</style>
